import Decimal from 'decimal.js';

export interface QuantityRange {
   from: number;
   /** Upper bound; without it the range is the single quantity `from`. */
   to?: number;
   palletSize?: number;
   /** Packaging net weight: below the pallet size only whole multiples are orderable. */
   increment?: number;
}

export interface QuantityNeighbours {
   isValid: boolean;
   lower?: number;
   higher?: number;
}

/** Orderable quantities within [from, to]: multiples of `step`, or every quantity when there is no step. */
interface StepSequence {
   from: Decimal;
   to: Decimal;
   step?: Decimal;
}

export const isMultipleOf = (value: number, increment: number): boolean => new Decimal(value).mod(increment).isZero();

const floorToStep = (value: Decimal, step: Decimal): Decimal => value.minus(value.mod(step));

const getStepSequences = ({ from, to, palletSize, increment }: QuantityRange): StepSequence[] => {
   const start = new Decimal(from);
   const startOnly: StepSequence = { from: start, to: start };
   if (to === undefined || to <= from) return [startOnly];

   const end = new Decimal(to);
   const pallet = palletSize !== undefined && palletSize > 0 ? new Decimal(palletSize) : undefined;

   // Below the pallet size: whole packagings, or any quantity when no packaging increment is known.
   const belowPallet: StepSequence = {
      from: start,
      to: pallet ? Decimal.min(end, pallet) : end,
      step: increment !== undefined && increment > 0 ? new Decimal(increment) : undefined
   };

   return pallet ? [startOnly, belowPallet, { from: start, to: end, step: pallet }] : [startOnly, belowPallet];
};

const contains = ({ from, to, step }: StepSequence, quantity: Decimal): boolean =>
   quantity.gte(from) && quantity.lte(to) && (step === undefined || quantity.mod(step).isZero());

const lowerThan = ({ from, to, step }: StepSequence, quantity: Decimal): Decimal | undefined => {
   if (step === undefined) return quantity.gt(to) ? to : undefined;

   const below = quantity.mod(step).isZero() ? quantity.minus(step) : floorToStep(quantity, step);
   const value = Decimal.min(below, floorToStep(to, step));
   return value.gte(Decimal.max(from, step)) ? value : undefined;
};

const higherThan = ({ from, to, step }: StepSequence, quantity: Decimal): Decimal | undefined => {
   if (step === undefined) return quantity.lt(from) ? from : undefined;

   const firstInRange = from.mod(step).isZero() ? from : floorToStep(from, step).plus(step);
   const value = Decimal.max(floorToStep(quantity, step).plus(step), firstInRange);
   return value.lte(to) ? value : undefined;
};

const closest = (values: (Decimal | undefined)[], pick: (...values: Decimal[]) => Decimal): number | undefined => {
   const defined = values.filter((value): value is Decimal => value !== undefined);
   return defined.length > 0 ? pick(...defined).toNumber() : undefined;
};

/**
 * Single source of truth for which quantities a quote range accepts: whether `q` is orderable and the
 * closest orderable quantity below and above it.
 */
export const getNearestValidQuantities = (range: QuantityRange, q: number): QuantityNeighbours => {
   const sequences = getStepSequences(range);
   const quantity = new Decimal(q);

   return {
      isValid: sequences.some((sequence) => contains(sequence, quantity)),
      lower: closest(sequences.map((sequence) => lowerThan(sequence, quantity)), (...values) => Decimal.max(...values)),
      higher: closest(sequences.map((sequence) => higherThan(sequence, quantity)), (...values) => Decimal.min(...values))
   };
};
